import type { ExamBoardConfig, ExamBoardId, ScoringScale } from "./types";

/**
 * Shared UAT-UK scoring philosophy: 1.0–9.0 per module/paper, MCQ, no negative
 * marking, no calculator. Both TMUA and ESAT inherit this; overrides per board
 * if they ever diverge.
 */
const UAT_UK_SCORING: ScoringScale = {
  min: 1.0,
  max: 9.0,
  step: 0.1,
  negativeMarking: false,
  calculatorAllowed: false,
};

const TMUA: ExamBoardConfig = {
  id: "TMUA",
  slug: "tmua",
  name: "Test of Mathematics for University Admission",
  shortName: "TMUA",
  provider: "UAT-UK / Pearson VUE",
  providerUrl: "https://esat-tmua.ac.uk/",
  status: "active",
  domains: ["tmua.fyi"],
  modulesSat: { min: 2, max: 2 },
  modules: [
    {
      id: "paper1",
      name: "Paper 1 — Applications of Mathematical Knowledge",
      shortName: "Paper 1",
      classification: "skill",
      questionCount: 20,
      durationMinutes: 75,
      mandatory: true,
      syllabusPrefixes: ["MM"],
      focus:
        "Paper 1 hands you familiar A-level pure content and asks you to apply it quickly. The mathematics is not the hard part: the difficulty is spotting which tool the question wants and reaching the answer by hand. Questions are built so that a well-chosen route takes under a minute and a brute-force route takes five.",
    },
    {
      id: "paper2",
      name: "Paper 2 — Mathematical Reasoning",
      shortName: "Paper 2",
      classification: "skill",
      questionCount: 20,
      durationMinutes: 75,
      mandatory: true,
      syllabusPrefixes: ["Arg", "Prf", "Err"],
      focus:
        "Paper 2 tests how you reason about mathematics rather than how much of it you can do. Questions turn on logic and proof: what follows from what, whether a condition is necessary or sufficient, what a single counterexample destroys, and where a printed argument breaks down. Very few candidates are taught this explicitly at school, which makes it the paper where deliberate preparation moves a score most.",
    },
  ],
  scoring: UAT_UK_SCORING,
  // Estimated: no official Pearson-era conversion is public. Anchors are the
  // only public raw→scaled table (2023 old-scale, CUP&A FOI release): 14/40
  // (35%) → 4.5 and 26/40 (65%) → 7.0. National percentiles are the UAT-UK
  // October 2024/25 cohort quantiles. Both via the tmua.fyi annual report.
  scoreConversion: {
    anchors: [
      { pct: 35, band: 4.5 },
      { pct: 65, band: 7.0 },
    ],
    nationalPercentiles: [
      { percentile: 25, band: 3.3 },
      { percentile: 50, band: 4.5 },
      { percentile: 75, band: 5.7 },
      { percentile: 90, band: 7.0 },
    ],
    reference: "estimated from public TMUA data (October cohort)",
  },
  universities: [
    {
      name: "Cambridge",
      fullName: "University of Cambridge",
      logo: "/cambridge.png",
      requirement: {
        slug: "cambridge",
        status: "required",
        overview:
          "Cambridge requires the TMUA for Computer Science, Economics and Mathematics. A strong result strengthens an application that is decided holistically: your predicted grades, personal statement, school reference and interview all count alongside the test. Mathematics applicants also sit STEP as part of their conditional offer. Cambridge applicants take the TMUA in the October window.",
        courses: [
          { name: "Computer Science", slug: "computer-science" },
          { name: "Economics", slug: "economics" },
          { name: "Mathematics", slug: "mathematics" },
        ],
        sitting: "Cambridge applicants must sit the TMUA in the October sitting.",
        officialUrl: "https://www.undergraduate.study.cam.ac.uk/apply/how/maths-admission-test",
      },
    },
    {
      name: "Oxford",
      fullName: "University of Oxford",
      logo: "/oxford.png",
      requirement: {
        slug: "oxford",
        status: "required",
        overview:
          "From 2027 entry, Oxford replaces the Maths Admissions Test (MAT) with the TMUA for its whole maths and computer science family. This is the biggest change to the test in years: the MAT was Oxford's own paper, with long written answers you worked through by hand, and the TMUA is a shared national test of 40 multiple-choice questions with no working marked. Anything written for the MAT is still worth doing as mathematics, but it no longer matches the format you will sit. Oxford applicants take the TMUA in the October window.",
        courses: [
          { name: "Mathematics", slug: "mathematics" },
          { name: "Mathematics and Statistics", slug: "mathematics-and-statistics" },
          { name: "Mathematics and Philosophy", slug: "mathematics-and-philosophy" },
          {
            name: "Mathematics and Computer Science",
            slug: "mathematics-and-computer-science",
          },
          { name: "Computer Science", slug: "computer-science" },
          { name: "Computer Science and Philosophy", slug: "computer-science-and-philosophy" },
        ],
        sitting:
          "Oxford applicants must sit the TMUA in the October sitting. The TMUA replaces the MAT from 2027 entry.",
        officialUrl: "https://www.maths.ox.ac.uk/study-here/undergraduate-study/maths-admissions-test",
      },
    },
    {
      name: "Imperial",
      fullName: "Imperial College London",
      logo: "/imperial.png",
      requirement: {
        slug: "imperial",
        status: "required",
        overview:
          "Imperial College London requires the TMUA for its Computing and Mathematics degrees and for BSc Economics, Finance and Data Science, and weighs the score directly when deciding who to make offers to. A strong performance carries real weight here. You can sit either the October or January test. Imperial uses the ESAT for its engineering degrees and the TMUA for these maths, computing and data courses.",
        courses: [
          { name: "Computing", slug: "computing" },
          { name: "Mathematics", slug: "mathematics" },
          { name: "Mathematics and Computer Science", slug: "mathematics-and-computer-science" },
          { name: "Economics, Finance and Data Science", slug: "economics-finance-and-data-science" },
        ],
        sitting: "Imperial accepts either the October or January sitting.",
        officialUrl:
          "https://www.imperial.ac.uk/study/apply/undergraduate/process/admissions-tests/tmua/",
      },
    },
    {
      name: "Warwick",
      fullName: "University of Warwick",
      logo: "/warwick.png",
      requirement: {
        slug: "warwick",
        status: "required",
        overview:
          "Warwick requires the TMUA for Computer Science and Mathematics, where it is used holistically alongside your grades (STEP is an accepted alternative for Mathematics). For Economics, MORSE and related degrees the TMUA is optional, but a strong score can earn a reduced offer. Warwick sets its score thresholds after results each year and does not publish them in advance. You can sit in either October or January.",
        courses: [
          { name: "Computer Science", slug: "computer-science" },
          { name: "Mathematics", slug: "mathematics" },
        ],
        sitting: "You can sit in October or January. STEP is an accepted alternative for Mathematics.",
        officialUrl: "https://warwick.ac.uk/study/undergraduate/apply/tmua/",
      },
    },
    {
      name: "Durham",
      fullName: "Durham University",
      logo: "/durham.png",
      requirement: {
        slug: "durham",
        status: "recommended",
        overview:
          "Durham recommends rather than requires the TMUA for its Mathematics degrees. If you score 5.0 or above you are automatically considered for an offer one grade below the standard (A*AA instead of A*A*A); a lower score is still viewed positively and is never counted against you. You must authorise Durham to access your result. That makes sitting the test a low-risk way to strengthen your application. You can sit in either October or January.",
        courses: [{ name: "Mathematics", slug: "mathematics" }],
        sitting: "You can sit in October or January. A score of 5.0 or above earns consideration for a reduced offer.",
        officialUrl: "https://www.durham.ac.uk/departments/academic/mathematical-sciences/",
      },
    },
    {
      name: "LSE",
      fullName: "London School of Economics",
      logo: "/lse.png",
      requirement: {
        slug: "lse",
        status: "required",
        overview:
          "The London School of Economics requires the TMUA for BSc Economics and BSc Econometrics and Mathematical Economics, and recommends it for its other maths and economics degrees. LSE selects from an exceptionally strong applicant pool without interviews, so the TMUA is one of the few objective signals it has, and it is read carefully alongside your grades and personal statement. You can sit in either October or January.",
        courses: [
          { name: "Economics", slug: "economics" },
          { name: "Econometrics and Mathematical Economics", slug: "econometrics-and-mathematical-economics" },
        ],
        sitting: "You can sit in October or January.",
        officialUrl:
          "https://www.lse.ac.uk/study-at-lse/Undergraduate/Prospective-Students/How-to-Apply/Test-of-Mathematics-for-University-Admission-TMUA",
      },
    },
    {
      name: "UCL",
      fullName: "University College London",
      logo: "/ucl.png",
      requirement: {
        slug: "ucl",
        status: "required",
        overview:
          "UCL requires the TMUA for its Economics BSc (Econ) and Economics with a Year Abroad degrees. It is not used for other UCL courses. The score is read alongside the rest of your UCAS application when UCL selects from a large and competitive field. You can sit in either October or January.",
        courses: [
          { name: "Economics (BSc Econ)", slug: "economics" },
          { name: "Economics with a Year Abroad", slug: "economics-with-a-year-abroad" },
        ],
        sitting: "You can sit in October or January.",
        officialUrl: "https://www.ucl.ac.uk/prospective-students/undergraduate/degrees/economics-bsc-econ-2026",
      },
    },
  ],
  theme: "tmua",
  // TMUA is a maths test, so it also surfaces the maths content of adjacent
  // admissions tests: ECAA (economics maths), MAT (Oxford maths), and the maths
  // portions of ENGAA/NSAA. The backend scopes ENGAA/NSAA to maths only.
  pastPaperSources: ["TMUA", "ENGAA", "NSAA", "ECAA", "MAT"],
  registration: {
    entry: "2027",
    opens: "2026-07-20",
    deadline: "2026-09-28",
    results: "November 2026",
    url: "https://esat-tmua.ac.uk/register/",
    fee: { uk: 78, international: 133 },
  },
  sittings: [
    {
      key: "october",
      label: "October sitting",
      date: "2026-10-12",
      endDate: "2026-10-16",
      results: "2026-11-16",
    },
    {
      key: "january",
      label: "January sitting",
      date: "2027-01-04",
      endDate: "2027-01-08",
      bookingDeadline: "2026-12-21",
      results: "2027-02-08",
      note: "Open to applicants to every university that uses the TMUA except Cambridge and Oxford, whose applicants sit in October. The exceptions are Cambridge mature colleges and the Oxford Foundation Year, which have January admissions deadlines of their own.",
    },
  ],
  replacedTest: {
    shortName: "MAT",
    name: "Maths Admissions Test",
    slug: "tmua-vs-mat",
    university: "Oxford",
    lastSat: "October 2025",
    lastEntryYear: "2026",
    firstEntryYear: "2027",
    summary:
      "Oxford retired the Maths Admissions Test after the October 2025 sitting. Everyone applying for 2027 entry sits the TMUA instead, and the MAT will not run again.",
    whatChanged: [
      "The last MAT was sat in October 2025, by people applying for 2026 entry. Anyone applying in autumn 2026 to start in 2027 sits the TMUA, across every Oxford mathematics and computer science course. Most of the confusion about this comes down to which year is being named, because pages written for an earlier cycle still describe the MAT as Oxford's test and were never updated.",
      "The change is larger than swapping one paper for another. The MAT was Oxford's own test, set and marked in Oxford, and its two long questions were read by an examiner who could give credit for a partial argument. The TMUA is a national test that several universities share, and none of your working is seen: each paper is twenty multiple-choice questions, and only the option you select counts.",
      "The total time is almost identical, but it is split across two papers of seventy-five minutes, which leaves a little under four minutes a question where the MAT gave you five and a half. The pace, rather than the syllabus, is what most people notice first.",
    ],
    preparation: [
      "Most of what you did for the MAT still counts. The two syllabuses are close, since both stop at roughly the first year of A level Maths and neither expects Further Maths, so the algebra, the graph sketching, the polynomial and logarithm work and the counting arguments all carry over. The multiple-choice section of a MAT paper is close to ideal TMUA practice, being the same content in the same format, and old MAT papers are worth working through for that section alone.",
      "What does not carry over is the long questions and the habits they encouraged. On the MAT it was rational to spend fifteen minutes building a careful written argument, because the argument was what earned the marks. On the TMUA that time does not exist and the argument is never read, so a question you cannot see a route through within a few minutes is one to leave and come back to.",
      "The larger gap is Paper 2. The MAT tested reasoning implicitly, through the shape of its long questions, whereas the TMUA gives it a paper of its own: necessary and sufficient conditions, what a single counterexample settles, and where a printed argument breaks down. Almost nobody meets this explicitly at school and nothing in MAT preparation covers it directly, so it is usually where the remaining work is.",
    ],
    comparison: [
      { label: "Papers", retired: "One paper", current: "Two papers" },
      {
        label: "Time",
        retired: "2 hours 30 minutes",
        current: "75 minutes per paper, 2 hours 30 minutes in total",
      },
      {
        label: "Questions",
        retired: "27: 25 multiple choice and 2 long questions",
        current: "40: 20 multiple choice per paper",
      },
      {
        label: "Your working",
        retired: "Read on the two long questions, with partial credit",
        current: "Never seen. Only the option you select is marked",
      },
      { label: "Marks", retired: "Out of 100", current: "Each paper scored from 1.0 to 9.0" },
      { label: "Calculator", retired: "Not allowed", current: "Not allowed" },
      {
        label: "Content",
        retired: "The first year of A level Maths, plus a few later topics",
        current: "Paper 1 applies pure maths at the same level; Paper 2 tests logic and proof",
      },
    ],
    faqs: [
      {
        question: "Does Oxford still use the MAT?",
        answer:
          "No. The last MAT was sat in October 2025, for 2026 entry, and it will not run again. Oxford uses the TMUA from 2027 entry onwards for all of its mathematics and computer science courses.",
      },
      {
        question: "Is the TMUA harder than the MAT?",
        answer:
          "Neither syllabus goes deeper than the other, so they are hard in different ways rather than one being harder. The MAT gave you more time per question and marks for partial working, which suited candidates who reason carefully and slowly. The TMUA gives you neither, so it rewards speed and confidence in an answer you cannot show working for.",
      },
      {
        question: "Are MAT past papers still useful?",
        answer:
          "Yes, particularly the multiple-choice section, which is the same syllabus in the same format as the TMUA. The long questions are still good mathematics and good interview practice, but they train you to work at a pace the TMUA does not allow, so treat them as maths rather than as timed preparation.",
      },
      {
        question: "Why did Oxford drop the MAT?",
        answer:
          "Oxford moved its admissions testing onto the shared UAT-UK system run with Pearson VUE, which also absorbed the Physics Aptitude Test and the Thinking Skills Assessment. For candidates the practical effect is one fewer Oxford-specific test to prepare for, since the TMUA is already required at several other universities.",
      },
    ],
    officialUrl: "https://www.maths.ox.ac.uk/study-here/undergraduate-study/tmua",
  },
};

const ESAT: ExamBoardConfig = {
  id: "ESAT",
  slug: "esat",
  name: "Engineering and Science Admissions Test",
  shortName: "ESAT",
  provider: "UAT-UK / Pearson VUE",
  providerUrl: "https://esat-tmua.ac.uk/",
  status: "active",
  domains: ["esat.fyi"],
  // Maths 1 is mandatory; candidates sit 2–3 modules depending on the course.
  modulesSat: { min: 2, max: 3 },
  modules: [
    {
      id: "maths1",
      name: "Mathematics 1",
      shortName: "Maths 1",
      classification: "subject",
      questionCount: 27,
      durationMinutes: 40,
      mandatory: true,
      topicPrefix: "M",
      color: "#4F46E5",
      focus:
        "The one module every candidate sits, whatever the course. Number, algebra, ratio, geometry and the manipulation you are expected to do accurately without a calculator. Nothing in it goes beyond AS-level; the difficulty is entirely in the pace.",
    },
    {
      id: "maths2",
      name: "Mathematics 2",
      shortName: "Maths 2",
      classification: "subject",
      questionCount: 27,
      durationMinutes: 40,
      mandatory: false,
      topicPrefix: "MM",
      color: "#0891B2",
      focus:
        "The advanced maths module: functions, coordinate geometry, sequences and series, trigonometry, logarithms, differentiation and integration. Most engineering and physics courses ask for it alongside Mathematics 1, so check your course before you plan around skipping it.",
    },
    {
      id: "biology",
      name: "Biology",
      shortName: "Biology",
      classification: "subject",
      questionCount: 27,
      durationMinutes: 40,
      mandatory: false,
      topicPrefix: "B",
      color: "#15803D",
      focus:
        "Cells, physiology, genetics and inheritance, ecosystems and variation, at roughly A-level standard. Chosen mostly by Natural Sciences and Veterinary Medicine applicants, and the only module where recall carries real weight.",
    },
    {
      id: "chemistry",
      name: "Chemistry",
      shortName: "Chemistry",
      classification: "subject",
      questionCount: 27,
      durationMinutes: 40,
      mandatory: false,
      topicPrefix: "C",
      color: "#BE123C",
      focus:
        "Atomic structure, bonding, energetics, kinetics, equilibria, organic chemistry and quantitative work. The calculations are where the time goes, and you have no calculator for them, so fluency with moles and ratios by hand matters more here than anywhere else on the paper.",
    },
    {
      id: "physics",
      name: "Physics",
      shortName: "Physics",
      classification: "subject",
      questionCount: 27,
      durationMinutes: 40,
      mandatory: false,
      topicPrefix: "P",
      color: "#D97706",
      focus:
        "Mechanics, electricity, magnetism, waves, radioactivity and matter. Nearly every engineering course and Physics itself require it, so for most candidates it is a third compulsory module in practice rather than a choice.",
    },
  ],
  scoring: UAT_UK_SCORING,
  universities: [
    {
      name: "Cambridge",
      fullName: "University of Cambridge",
      logo: "/cambridge.png",
      requirement: {
        slug: "cambridge",
        status: "required",
        overview:
          "Cambridge uses the ESAT for Engineering, Chemical Engineering and Biotechnology, Natural Sciences and Veterinary Medicine. The ESAT replaced the NSAA and ENGAA that Cambridge used previously, so past papers from those tests remain the closest available practice. You sit Mathematics 1 plus two further modules chosen to match your course, all in the October window, and the result feeds a holistic decision that includes your interview.",
        courses: [
          { name: "Engineering", slug: "engineering", modules: ["maths1", "maths2", "physics"] },
          {
            name: "Chemical Engineering and Biotechnology",
            slug: "chemical-engineering-and-biotechnology",
            modules: ["maths1"],
            moduleNote: "Plus any two of Mathematics 2, Physics, Chemistry or Biology, chosen to match your current studies.",
          },
          {
            name: "Natural Sciences",
            slug: "natural-sciences",
            modules: ["maths1"],
            moduleNote: "Plus any two of Mathematics 2, Physics, Chemistry or Biology, chosen to match your current studies.",
          },
          {
            name: "Veterinary Medicine",
            slug: "veterinary-medicine",
            modules: ["maths1"],
            moduleNote: "Plus any two of Mathematics 2, Physics, Chemistry or Biology, chosen to match your current studies.",
          },
        ],
        sitting: "Cambridge applicants must sit the ESAT in the October sitting.",
        officialUrl:
          "https://www.undergraduate.study.cam.ac.uk/apply/how/science-engineering-admission-test",
      },
    },
    {
      name: "Imperial",
      fullName: "Imperial College London",
      logo: "/imperial.png",
      requirement: {
        slug: "imperial",
        status: "required",
        overview:
          "Imperial College London requires the ESAT for most of its Engineering degrees and Physics, and weighs the score heavily when deciding who to interview and make offers to. Note that Computing uses the TMUA instead, and Bioengineering, Materials, and Earth Science currently use no admissions test, so check your specific course. You can sit in October or January, unless you are also applying to Cambridge, in which case you must sit in October.",
        courses: [
          { name: "Aeronautics", slug: "aeronautics", modules: ["maths1", "maths2", "physics"] },
          { name: "Chemical Engineering", slug: "chemical-engineering", modules: ["maths1", "maths2", "chemistry"] },
          {
            name: "Civil and Environmental Engineering",
            slug: "civil-and-environmental-engineering",
            modules: ["maths1", "maths2", "physics"],
          },
          {
            name: "Design Engineering",
            slug: "design-engineering",
            modules: ["maths1", "maths2"],
            moduleNote: "Design Engineering sits only two modules: Mathematics 1 and Mathematics 2.",
          },
          {
            name: "Electrical and Electronic Engineering",
            slug: "electrical-and-electronic-engineering",
            modules: ["maths1", "maths2", "physics"],
          },
          { name: "Mechanical Engineering", slug: "mechanical-engineering", modules: ["maths1", "maths2", "physics"] },
          { name: "Physics", slug: "physics", modules: ["maths1", "maths2", "physics"] },
        ],
        sitting:
          "Imperial accepts the October or January sitting, but you must sit in October if you are also applying to Cambridge.",
        officialUrl:
          "https://www.imperial.ac.uk/study/apply/undergraduate/process/admissions-tests/esat/",
      },
    },
    {
      name: "UCL",
      fullName: "University College London",
      logo: "/ucl.png",
      requirement: {
        slug: "ucl",
        status: "required",
        overview:
          "UCL requires the ESAT only for its Electronic and Electrical Engineering degrees (BEng H600 and MEng H601), from 2027 entry onwards (applications in autumn 2026). It is not required for any other UCL course. Applicants sit Mathematics 1 plus any two of Physics, Mathematics 2, Chemistry or Biology, with exemptions for students progressing from UCL's own foundation programmes.",
        courses: [
          {
            name: "Electronic and Electrical Engineering (BEng, H600)",
            slug: "electronic-and-electrical-engineering-beng",
            modules: ["maths1"],
            moduleNote: "Plus any two of Mathematics 2, Physics, Chemistry or Biology.",
          },
          {
            name: "Electronic and Electrical Engineering (MEng, H601)",
            slug: "electronic-and-electrical-engineering-meng",
            modules: ["maths1"],
            moduleNote: "Plus any two of Mathematics 2, Physics, Chemistry or Biology.",
          },
        ],
        sitting: "You can sit in October or January. The requirement applies from 2027 entry.",
        officialUrl:
          "https://www.ucl.ac.uk/engineering/esat-ucl-electronic-engineering-bengmeng-applicants-0",
      },
    },
    {
      name: "Oxford",
      fullName: "University of Oxford",
      logo: "/oxford.png",
      requirement: {
        slug: "oxford",
        status: "required",
        overview:
          "From 2027 entry, Oxford replaces the Physics Aptitude Test (PAT) with the ESAT for Physics, Engineering Science, and Physics and Philosophy. This is a major change: Oxford applicants now sit the same UAT-UK test already used by Cambridge and Imperial, so ESAT preparation counts across all three. Materials Science no longer requires an admissions test at all. Oxford applicants sit the ESAT in the October window.",
        courses: [
          { name: "Physics", slug: "physics", modules: ["maths1", "maths2", "physics"] },
          { name: "Engineering Science", slug: "engineering-science", modules: ["maths1", "maths2", "physics"] },
          { name: "Physics and Philosophy", slug: "physics-and-philosophy", modules: ["maths1", "maths2", "physics"] },
        ],
        sitting:
          "From 2027 entry (applications in autumn 2026), the ESAT replaces the Physics Aptitude Test (PAT). Oxford applicants sit in October. Materials Science no longer requires an admissions test.",
        officialUrl:
          "https://www.physics.ox.ac.uk/study/undergraduates/how-apply/engineering-and-science-admissions-test-esat",
      },
    },
  ],
  theme: "esat",
  pastPaperSources: ["ESAT", "ENGAA", "NSAA", "TMUA"],
  registration: {
    entry: "2027",
    opens: "2026-07-20",
    deadline: "2026-09-28",
    results: "November 2026",
    url: "https://esat-tmua.ac.uk/register/",
    fee: { uk: 78, international: 133 },
  },
  sittings: [
    {
      key: "october",
      label: "October sitting",
      date: "2026-10-12",
      endDate: "2026-10-16",
      results: "2026-11-16",
    },
    {
      key: "january",
      label: "January sitting",
      date: "2027-01-04",
      endDate: "2027-01-08",
      bookingDeadline: "2026-12-21",
      results: "2027-02-08",
      note: "Open to Imperial and UCL applicants. Cambridge and Oxford applicants sit in October, apart from Cambridge mature colleges and the Oxford Foundation Year, which have January admissions deadlines of their own.",
    },
  ],
  replacedTest: {
    shortName: "PAT",
    name: "Physics Aptitude Test",
    slug: "esat-vs-pat",
    university: "Oxford",
    lastSat: "October 2025",
    lastEntryYear: "2026",
    firstEntryYear: "2027",
    summary:
      "Oxford retired the Physics Aptitude Test after the October 2025 sitting. Physics, Engineering Science and Physics and Philosophy applicants for 2027 entry sit the ESAT instead, and Materials Science no longer requires a test at all.",
    whatChanged: [
      "The last PAT was sat in October 2025, by people applying for 2026 entry. Anyone applying in autumn 2026 to start in 2027 sits the ESAT, and there is no PAT sitting left to register for. Materials Science, which also used the PAT, has dropped its admissions test entirely rather than moving across.",
      "If you had already started preparing, this is a smaller change than it sounds. The PAT had itself moved to a fully multiple-choice, computer-based paper in 2024, so the ESAT is not the first version of this test where nobody reads your working. Two things are genuinely different. The PAT was two hours of physics and maths mixed together and you decided as you went where the time went, whereas the ESAT splits the same ground into separate forty-minute modules, so time saved on maths cannot be spent on physics. And the PAT provided an on-screen calculator, while the ESAT provides none, which changes what arithmetic you need to be fluent in rather than what physics you need to know.",
      "One consequence is worth knowing if you are applying widely. Cambridge and Imperial already used the ESAT, so preparation aimed at Oxford now counts at all three instead of only at Oxford.",
    ],
    preparation: [
      "PAT past papers are still the closest thing to ESAT physics practice that exists, and the 2024 and 2025 papers most of all, since those are the multiple-choice ones. Two adjustments make them behave like the test you will actually sit: work them in forty-minute blocks rather than as one two-hour paper, and do them without a calculator. The second is where most people find they are slower than they thought.",
      "The older PAT papers, the ones with long written answers, do not carry over as format practice. They are good physics and they are worth doing before an interview, where you will be asked to think out loud in exactly that way, but they train you to spend ten minutes on a problem the ESAT expects in about ninety seconds.",
      "The balance of the test has also shifted. Oxford asks for Mathematics 1, Mathematics 2 and Physics, so two of your three modules are maths, a larger share than the PAT gave it. If your preparation so far has been physics-led, that is the part of the plan to change first.",
    ],
    comparison: [
      {
        label: "Papers",
        retired: "One paper",
        current: "Two or three modules, depending on your course",
      },
      { label: "Time", retired: "2 hours", current: "40 minutes per module" },
      {
        label: "Questions",
        retired: "40 multiple choice",
        current: "27 multiple choice per module",
      },
      { label: "Marks", retired: "Out of 100", current: "Each module scored from 1.0 to 9.0" },
      {
        label: "Calculator",
        retired: "An on-screen calculator was provided",
        current: "None, on any module",
      },
      {
        label: "Content",
        retired: "Physics and maths mixed together through one paper",
        current: "Split into Mathematics 1, Mathematics 2 and Physics, each timed separately",
      },
      {
        label: "Wrong answers",
        retired: "Not penalised",
        current: "Not penalised",
      },
    ],
    faqs: [
      {
        question: "Does Oxford still use the PAT?",
        answer:
          "No. The last PAT was sat in October 2025, for 2026 entry, and there is no sitting left to register for. Physics, Engineering Science and Physics and Philosophy applicants sit the ESAT from 2027 entry onwards.",
      },
      {
        question: "Does Materials Science at Oxford still need an admissions test?",
        answer:
          "No. Materials Science used the PAT and did not move across to the ESAT, so it now requires no admissions test. Requirements are set course by course, so check the course page before you plan around it.",
      },
      {
        question: "Is the ESAT easier than the PAT?",
        answer:
          "The content sits at a similar level, so the difference is in how it is sat. The ESAT is broken into short timed modules and gives you no calculator, both of which reward arithmetic fluency and pace rather than stamina over a long paper. Candidates who found two hours the hard part tend to prefer it, and candidates who leaned on the calculator tend not to.",
      },
      {
        question: "Can I use PAT past papers to prepare for the ESAT?",
        answer:
          "Yes. Start with the 2024 and 2025 papers, which are multiple choice like the ESAT, and sit them in forty-minute blocks without a calculator. The older written papers are worth doing as physics and as interview practice, but not as timed preparation.",
      },
      {
        question: "Why did Oxford drop the PAT?",
        answer:
          "Oxford moved its admissions testing onto the shared UAT-UK system run with Pearson VUE, which also absorbed the Maths Admissions Test and the Thinking Skills Assessment. For candidates the practical effect is that Oxford, Cambridge and Imperial now use the same test, so one preparation covers all three.",
      },
    ],
    officialUrl:
      "https://www.physics.ox.ac.uk/study/undergraduates/how-apply/engineering-and-science-admissions-test-esat",
  },
};

/**
 * Retired (2023). Modelled so historical NSAA content and NSAA→ESAT/TMUA topic
 * mappings remain first-class. Not served on any live domain.
 */
const NSAA: ExamBoardConfig = {
  id: "NSAA",
  slug: "nsaa",
  name: "Natural Sciences Admissions Assessment",
  shortName: "NSAA",
  provider: "Cambridge Assessment Admissions Testing (retired)",
  status: "retired",
  domains: [],
  modulesSat: { min: 2, max: 2 },
  modules: [
    {
      id: "section1",
      name: "Section 1 — Mathematics + chosen science",
      shortName: "Section 1",
      classification: "subject",
      questionCount: 40,
      durationMinutes: 60,
      mandatory: true,
    },
    {
      id: "section2",
      name: "Section 2 — Advanced chosen science",
      shortName: "Section 2",
      classification: "subject",
      questionCount: 20,
      durationMinutes: 60,
      mandatory: true,
    },
  ],
  scoring: {
    min: 1.0,
    max: 9.0,
    step: 0.1,
    negativeMarking: false,
    calculatorAllowed: false,
  },
  universities: [
    { name: "Cambridge", fullName: "University of Cambridge", logo: "/cambridge.png" },
  ],
  theme: "tmua",
  pastPaperSources: ["NSAA"],
};

export const EXAM_BOARDS = { TMUA, ESAT, NSAA } as const satisfies Record<
  ExamBoardId,
  ExamBoardConfig
>;

/** The board served when a host can't be resolved (local dev, previews, etc.). */
export const DEFAULT_BOARD_ID: ExamBoardId = "TMUA";
